import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Toolbar,
  EditSettingsModel,
} from '@syncfusion/ej2-react-grids';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { OkButton } from '@components/Form/OkButton';
import { FilterRecord, TableActions } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { routes } from '@config/routes';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import { CustomerService } from '@services/Customer';

export const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useNotification();
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0);
  const [showDelete, setShowDelete] = useState(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [queryFilter, setQueryFilter] = useState<string>('');

  // Create a service wrapper that matches useSyncfusionGrid interface
  const customerServiceWrapper = {
    ...CustomerService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return CustomerService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
  } = useSyncfusionGrid({
    service: customerServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  // Custom hooks for grid functionality
  const { filters, updateFilter, clearFilters, applyFilters } = useGridFilters(
    { companyNameFilter: '' },
    setQueryFilter
  );

  const { handleDelete: handleDeleteOperation } = useGridOperations({
    service: CustomerService,
    refreshGrid: fetchData,
    entityName: 'customer',
  });

  const editOptions: EditSettingsModel = {
    allowAdding: true,
    allowDeleting: true,
    allowEditing: true,
    mode: 'Dialog',
  };

  const handleDelete = async (rowId: any) => {
    try {
      const result = await CustomerService.getById(Number(rowId));
      if (result.isSuccess) {
        setSelectedCustomerId(rowId);
        setCustomerName('data' in result ? result.data.companyName : '');
        setShowDelete(true);
      } else {
        const message = `Failed to fetch customer details for deletion. ${result.errorMessages}`;
        showError(message);
        console.error(message);
      }
    } catch (error) {
      console.error('Error: ', error);
    }
  };

  const confirmDelete = () => {
    handleDeleteOperation(selectedCustomerId, customerName);
    setShowDelete(false);
  };

  const handleEdit = (rowId: any) => {
    navigate(`${routes.RNC_CUSTOMERS}/${rowId}/edit`);
  };

  const handleView = (rowId: any) => {
    navigate(`${routes.RNC_CUSTOMERS}/${rowId}`);
  };

  const handleSendStatement = (rowId: number) => {
    console.log('Send statement to customer', rowId);
    showSuccess(`Statement sent to customer`);
  };

  const handleCreateInvoice = (rowId: number) => {
    navigate(`${routes.RNC_INVOICES}/create`, {
      state: { customerID: rowId, from: location },
    });
  };

  const actionButtons = (props: any) => {
    if (!props || !props.id) return null;
    return (
      <TableActions
        inlineActions={[
          {
            title: 'Create Invoice',
            onClick: () => handleCreateInvoice(props.id),
          },
        ]}
        dropdownActions={[
          {
            title: 'Send Statement',
            onClick: () => handleSendStatement(props.id),
          },
          {
            title: 'View',
            onClick: () => handleView(props.id),
          },
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
          },
          {
            showTopSeparator: true,
            title: 'Delete',
            onClick: () => handleDelete(props.id),
          },
        ]}
      />
    );
  };

  return (
    <>
      <FilterRecord
        label="Filter by Name"
        value={filters.companyNameFilter || ''}
        onChange={(e) => updateFilter('companyNameFilter', e.target.value)}
        onFilter={() => {
          applyFilters(() => fetchData(true));
        }}
        onClear={() => {
          clearFilters();
          applyFilters(() => fetchData(true));
        }}
      />

      <Space />

      <GridComponent
        key={gridKey}
        dataBound={renderComplete}
        dataSource={gridData}
        ref={gridRef}
        allowPaging={true}
        pageSettings={{
          pageSize: 10,
          pageSizes: [5, 10, 20, 50, 100],
        }}
        editSettings={editOptions}
        dataStateChange={dataStateChange}
      >
        <ColumnsDirective>
          <ColumnDirective field="companyName" headerText="Name" />
          <ColumnDirective field="primaryContact.email" headerText="Email" />
          <ColumnDirective field="primaryContact.phone" headerText="Phone" />

          <ColumnDirective
            headerText="Actions"
            width={200}
            template={actionButtons}
          />
        </ColumnsDirective>
        <Inject services={[Page, Toolbar]} />
      </GridComponent>

      {showDelete && (
        <>
          <Modal show={true}>
            <CloseButton onClick={() => setShowDelete(false)} />
            <Box size="medium">
              <>
                <div className="px-4 pb-2">
                  <span>Are you sure to delete Customer: {customerName}</span>
                </div>
                <FormButtonsContainer>
                  <div className="space-x-2">
                    <CancelButton onClick={() => setShowDelete(false)} />
                    <OkButton onClick={confirmDelete}>Delete</OkButton>
                  </div>
                </FormButtonsContainer>
              </>
            </Box>
          </Modal>
        </>
      )}
    </>
  );
};
